import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { DataTable, fmtDateTime, fmtDur } from './usageShared';

/**
 * Call problems: how often calls fail, why, on which platform and for whom,
 * with a drill-down to one call's timeline. Diagnostics only.
 */

const PlatformLabel = { web: 'Web', mobile: 'Mobile', unknown: 'Unknown' };

const Timeline = ({ callId, onClose }) => {
    const [d, setD] = useState(null);
    const [err, setErr] = useState('');

    useEffect(() => {
        let live = true;
        api.get(`/admin-usage/call-issues/call/${callId}`)
            .then(r => { if (live) setD(r.data); })
            .catch(e => { if (live) setErr(e.response?.data?.message || 'Could not load this call.'); });
        return () => { live = false; };
    }, [callId]);

    return (
        <div className="ur-card" style={{ borderColor: 'var(--primary)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3>Call timeline</h3>
                <button type="button" className="ur-chip" onClick={onClose}>Close</button>
            </div>
            {err && <div className="ur-empty">{err}</div>}
            {!d && !err && <div className="ur-empty">Loading...</div>}
            {d && (
                <>
                    <div className="ur-hint">
                        {d.call.kind} call by {d.call.initiator || 'unknown'} · started {fmtDateTime(d.call.startedAt)} ·
                        {' '}ended as <strong>{d.call.endReason || 'still open'}</strong>
                        {d.call.failureReason ? ` (${d.call.failureReason})` : ''} · {fmtDur(d.call.durationSec)} ·
                        {' '}peak {d.call.peakParticipants} connected
                    </div>
                    <div className="ur-table-wrap">
                        <table className="ur-table">
                            <thead><tr><th>Person</th><th>Result</th><th>Joined</th><th>Left</th></tr></thead>
                            <tbody>
                                {d.call.participants.map((p, i) => (
                                    <tr key={i}>
                                        <td>{p.name || 'Unknown'}</td><td>{p.state}</td>
                                        <td>{fmtDateTime(p.joinedAt)}</td><td>{fmtDateTime(p.leftAt)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <h3 style={{ marginTop: 14 }}>Issues on this call</h3>
                    {d.issues.length === 0 ? <div className="ur-empty">No issues were recorded for this call.</div> : (
                        <div className="ur-table-wrap">
                            <table className="ur-table">
                                <thead><tr><th>Time</th><th>Who</th><th>From</th><th>Code</th><th>What happened</th><th>Detail</th></tr></thead>
                                <tbody>
                                    {d.issues.map(i => (
                                        <tr key={i._id}>
                                            <td>{fmtDateTime(i.at)}</td>
                                            <td>{i.user || '-'}</td>
                                            <td>{i.source} / {PlatformLabel[i.platform] || i.platform}</td>
                                            <td><span className="ur-pill">{i.code}{i.fatal ? ' (fatal)' : ''}</span></td>
                                            <td style={{ whiteSpace: 'normal', maxWidth: 320 }}>{i.message}</td>
                                            <td style={{ whiteSpace: 'normal', maxWidth: 360, fontSize: 11, color: 'var(--theme-muted)' }}>
                                                {i.detail ? JSON.stringify(i.detail) : '-'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

const CallBugsPanel = ({ from, to }) => {
    const [data, setData] = useState(null);
    const [error, setError] = useState('');
    const [openCall, setOpenCall] = useState(null);

    useEffect(() => {
        let live = true;
        setError('');
        api.get('/admin-usage/call-issues', { params: { from, to } })
            .then(r => { if (live) setData(r.data); })
            .catch(e => { if (live) setError(e.response?.data?.message || 'Could not load call issues.'); });
        return () => { live = false; };
    }, [from, to]);

    if (error) return <div className="ur-empty">{error}</div>;
    if (!data) return <div className="ur-empty">Loading...</div>;

    return (
        <>
            <div className="ur-hint">
                Problems reported by the call server and by web browsers. Mobile calls show up here only when the server
                itself refuses or ends a call; the mobile app does not report its own errors yet. Tracking starts from
                this release.
            </div>
            <div className="ur-kpis">
                <div className="ur-kpi">
                    <div className="ur-kpi-label">Failed calls</div>
                    <div className="ur-kpi-value">{data.failedCalls}</div>
                    <div className="ur-kpi-sub">{data.failedPct}% of {data.totalCalls} calls</div>
                </div>
                {data.byPlatform.map(p => (
                    <div className="ur-kpi" key={p.platform}>
                        <div className="ur-kpi-label">Issues on {PlatformLabel[p.platform] || p.platform}</div>
                        <div className="ur-kpi-value">{p.count}</div>
                        <div className="ur-kpi-sub">{p.fatal} ended the call</div>
                    </div>
                ))}
            </div>

            {openCall && <Timeline callId={openCall} onClose={() => setOpenCall(null)} />}

            <h3 style={{ margin: '4px 0 8px' }}>By cause</h3>
            <DataTable
                key="codes"
                rows={data.byCode}
                searchKeys={[r => r.code, r => r.sample]}
                csvName="call-issues-by-cause"
                empty="No call issues in this period."
                cols={[
                    { key: 'code', label: 'Cause', render: r => <span className="ur-pill">{r.code}</span> },
                    { key: 'count', label: 'Times', num: true },
                    { key: 'fatal', label: 'Ended call', num: true },
                    { key: 'users', label: 'People hit', num: true },
                    { key: 'source', label: 'Reported by' },
                    { key: 'sample', label: 'Example', render: r => <span style={{ whiteSpace: 'normal' }}>{r.sample}</span> },
                    { key: 'last', label: 'Last seen', render: r => fmtDateTime(r.last) }
                ]}
            />

            <h3 style={{ margin: '18px 0 8px' }}>Most affected people</h3>
            <DataTable
                key="users"
                rows={data.byUser}
                searchKeys={[r => r.name]}
                csvName="call-issues-by-user"
                empty="Nobody hit a recorded issue."
                cols={[
                    { key: 'name', label: 'Employee', render: r => r.name || 'Unknown' },
                    { key: 'count', label: 'Issues', num: true },
                    { key: 'fatal', label: 'Ended call', num: true },
                    { key: 'last', label: 'Last seen', render: r => fmtDateTime(r.last) }
                ]}
            />

            <h3 style={{ margin: '18px 0 8px' }}>Recent issues <span className="ur-hint">(click a row for the call timeline)</span></h3>
            <DataTable
                key="recent"
                rows={data.recent}
                searchKeys={[r => r.user?.name, r => r.code, r => r.message]}
                csvName="call-issues-recent"
                empty="No call issues in this period."
                onRowClick={r => r.call && setOpenCall(r.call)}
                cols={[
                    { key: 'createdAt', label: 'When', render: r => fmtDateTime(r.createdAt) },
                    { key: 'user', label: 'Who', value: r => r.user?.name, render: r => r.user?.name || '-' },
                    { key: 'platform', label: 'Platform', render: r => PlatformLabel[r.platform] || r.platform },
                    { key: 'code', label: 'Cause', render: r => <span className="ur-pill">{r.code}{r.fatal ? ' (fatal)' : ''}</span> },
                    { key: 'message', label: 'What happened', render: r => <span style={{ whiteSpace: 'normal' }}>{r.message}</span> }
                ]}
            />
        </>
    );
};

export default CallBugsPanel;
